import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, getErrorMessage } from '../services/api';
import { User, CreateUserRequest } from '../types';

export function UserManagement() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [users, setUsers] = useState<User[]>([]);

  // Form states
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUser, setNewUser] = useState<CreateUserRequest>({
    username: '',
    password: '',
    email: '',
  });

  // Edit states
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editForcePasswordReset, setEditForcePasswordReset] = useState(false);

  // Reset password states
  const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');
      const userList = await api.getUsers();
      setUsers(userList);
    } catch (err) {
      setError(`Failed to load users: ${getErrorMessage(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      await api.createUser(newUser);
      setSuccess('User created successfully');
      setShowNewUserForm(false);
      setNewUser({ username: '', password: '', email: '' });
      await loadData();
    } catch (err) {
      setError(`Failed to create user: ${getErrorMessage(err)}`);
    }
  };

  const handleActivate = async (userId: number) => {
    try {
      setError('');
      await api.activateUser(userId);
      setSuccess('User activated');
      await loadData();
    } catch (err) {
      setError(`Failed to activate user: ${getErrorMessage(err)}`);
    }
  };

  const handleDeactivate = async (userId: number) => {
    try {
      setError('');
      await api.deactivateUser(userId);
      setSuccess('User deactivated');
      await loadData();
    } catch (err) {
      setError(`Failed to deactivate user: ${getErrorMessage(err)}`);
    }
  };

  const handleDelete = async (userId: number, username: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This cannot be undone.`)) return;
    try {
      setError('');
      await api.deleteUser(userId);
      setSuccess('User deleted');
      await loadData();
    } catch (err) {
      setError(`Failed to delete user: ${getErrorMessage(err)}`);
    }
  };

  const handleStartEdit = (u: User) => {
    setEditingUserId(u.userId);
    setEditEmail(u.email);
    setEditForcePasswordReset(u.forcePasswordReset);
  };

  const handleSaveEdit = async (userId: number) => {
    try {
      setError('');
      await api.updateUser(userId, { email: editEmail, forcePasswordReset: editForcePasswordReset });
      setSuccess('User updated');
      setEditingUserId(null);
      await loadData();
    } catch (err) {
      setError(`Failed to update user: ${getErrorMessage(err)}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditEmail('');
  };

  const handleStartResetPassword = (userId: number) => {
    setResetPasswordUserId(userId);
    setNewPassword('');
  };

  const handleResetPassword = async (userId: number) => {
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }
    try {
      setError('');
      await api.resetUserPassword(userId, { newPassword });
      setSuccess('Password reset successfully');
      setResetPasswordUserId(null);
      setNewPassword('');
    } catch (err) {
      setError(`Failed to reset password: ${getErrorMessage(err)}`);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo} onClick={() => navigate('/')}>Uliji</h1>
          <h2 style={styles.pageTitle}>User Management</h2>
        </div>
        <div style={styles.userInfo}>
          <button onClick={() => navigate('/settings')} style={styles.settingsButton}>Settings</button>
          <span style={styles.username}>{user?.username}</span>
          <button onClick={logout} style={styles.logoutButton}>Logout</button>
        </div>
      </header>

      <main style={styles.main}>
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        <div style={styles.content}>
          <div style={styles.contentHeader}>
            <h3 style={styles.contentTitle}>Users</h3>
            <button onClick={() => setShowNewUserForm(true)} style={styles.addButton}>
              + Add User
            </button>
          </div>

          {showNewUserForm && (
            <form onSubmit={handleCreateUser} style={styles.form}>
              <h4 style={styles.formTitle}>New User</h4>
              <div style={styles.formGroup}>
                <label style={styles.label}>Username</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                  style={styles.input}
                  placeholder="Enter username"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  style={styles.input}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  style={styles.input}
                  placeholder="Enter password"
                  required
                />
              </div>
              <div style={styles.formActions}>
                <button type="submit" style={styles.saveButton}>Create User</button>
                <button type="button" onClick={() => setShowNewUserForm(false)} style={styles.cancelButton}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {isLoading ? (
            <div style={styles.loading}>Loading...</div>
          ) : users.length === 0 ? (
            <div style={styles.empty}>No users found</div>
          ) : (
            <div style={styles.usersList}>
              {users.map(u => (
                <div key={u.userId} style={styles.userCard}>
                  <div style={styles.userHeader}>
                    <span style={styles.userName}>{u.username}</span>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: u.isActive ? '#4caf50' : '#9e9e9e',
                    }}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {editingUserId === u.userId ? (
                    <div style={styles.editForm}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Email</label>
                        <input
                          type="email"
                          value={editEmail}
                          onChange={e => setEditEmail(e.target.value)}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.checkboxGroup}>
                        <label style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={editForcePasswordReset}
                            onChange={e => setEditForcePasswordReset(e.target.checked)}
                            style={styles.checkbox}
                          />
                          Force password reset on next login
                        </label>
                      </div>
                      <div style={styles.editActions}>
                        <button onClick={() => handleSaveEdit(u.userId)} style={styles.saveButton}>Save</button>
                        <button onClick={handleCancelEdit} style={styles.cancelButton}>Cancel</button>
                      </div>
                    </div>
                  ) : resetPasswordUserId === u.userId ? (
                    <div style={styles.editForm}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>New Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                          style={styles.input}
                          placeholder="Enter new password"
                        />
                      </div>
                      <div style={styles.editActions}>
                        <button onClick={() => handleResetPassword(u.userId)} style={styles.saveButton}>Reset Password</button>
                        <button onClick={() => setResetPasswordUserId(null)} style={styles.cancelButton}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={styles.userDetails}>
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>Email</span>
                          <span style={styles.detailValue}>{u.email}</span>
                        </div>
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>Created</span>
                          <span style={styles.detailValue}>{formatDate(u.createdAt)}</span>
                        </div>
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>Last Login</span>
                          <span style={styles.detailValue}>
                            {u.lastLoginAt ? formatDate(u.lastLoginAt) : 'Never'}
                          </span>
                        </div>
                        {u.forcePasswordReset && (
                          <div style={styles.detailRow}>
                            <span style={styles.detailLabel}>Password Reset</span>
                            <span style={styles.warningValue}>Required on next login</span>
                          </div>
                        )}
                      </div>
                      <div style={styles.userActions}>
                        <button onClick={() => handleStartEdit(u)} style={styles.editButton}>
                          Edit
                        </button>
                        <button onClick={() => handleStartResetPassword(u.userId)} style={styles.resetButton}>
                          Reset Password
                        </button>
                        {u.isActive ? (
                          <button onClick={() => handleDeactivate(u.userId)} style={styles.deactivateButton}>
                            Deactivate
                          </button>
                        ) : (
                          <button onClick={() => handleActivate(u.userId)} style={styles.activateButton}>
                            Activate
                          </button>
                        )}
                        <button onClick={() => handleDelete(u.userId, u.username)} style={styles.deleteButton}>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 32px',
    backgroundColor: '#16213e',
    borderBottom: '1px solid #233554',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  logo: {
    color: '#e94560',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
    cursor: 'pointer',
  },
  pageTitle: {
    color: '#ccd6f6',
    fontSize: '18px',
    fontWeight: 'normal',
    margin: 0,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  username: {
    color: '#ccd6f6',
  },
  settingsButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #233554',
    backgroundColor: 'transparent',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '14px',
  },
  logoutButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #e94560',
    backgroundColor: 'transparent',
    color: '#e94560',
    cursor: 'pointer',
    fontSize: '14px',
  },
  main: {
    padding: '32px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  error: {
    backgroundColor: 'rgba(233, 69, 96, 0.2)',
    border: '1px solid #e94560',
    color: '#e94560',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  success: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    border: '1px solid #4caf50',
    color: '#4caf50',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  content: {
    backgroundColor: '#16213e',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #233554',
  },
  contentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  contentTitle: {
    color: '#ccd6f6',
    fontSize: '18px',
    margin: 0,
  },
  addButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#e94560',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
  },
  loading: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '48px',
  },
  empty: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '48px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: '#1a1a2e',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #233554',
  },
  formTitle: {
    color: '#ccd6f6',
    fontSize: '16px',
    margin: '0 0 8px 0',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    color: '#8892b0',
    fontSize: '14px',
  },
  input: {
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #233554',
    backgroundColor: '#1a1a2e',
    color: '#ccd6f6',
    fontSize: '14px',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  saveButton: {
    padding: '10px 24px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#4caf50',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  cancelButton: {
    padding: '10px 24px',
    borderRadius: '6px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '14px',
  },
  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  userCard: {
    backgroundColor: '#1a1a2e',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #233554',
  },
  userHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  userName: {
    color: '#ccd6f6',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    color: 'white',
    fontWeight: 'bold',
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailLabel: {
    color: '#8892b0',
    fontSize: '13px',
  },
  detailValue: {
    color: '#ccd6f6',
    fontSize: '13px',
  },
  warningValue: {
    color: '#ff9800',
    fontSize: '13px',
    fontWeight: 'bold',
  },
  userActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #233554',
    flexWrap: 'wrap',
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #233554',
  },
  editActions: {
    display: 'flex',
    gap: '8px',
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  checkboxLabel: {
    color: '#ccd6f6',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  editButton: {
    padding: '6px 16px',
    borderRadius: '4px',
    border: '1px solid #2196f3',
    backgroundColor: 'transparent',
    color: '#2196f3',
    cursor: 'pointer',
    fontSize: '13px',
  },
  resetButton: {
    padding: '6px 16px',
    borderRadius: '4px',
    border: '1px solid #9c27b0',
    backgroundColor: 'transparent',
    color: '#9c27b0',
    cursor: 'pointer',
    fontSize: '13px',
  },
  activateButton: {
    padding: '6px 16px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#4caf50',
    color: 'white',
    cursor: 'pointer',
    fontSize: '13px',
  },
  deactivateButton: {
    padding: '6px 16px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#ff9800',
    color: 'white',
    cursor: 'pointer',
    fontSize: '13px',
  },
  deleteButton: {
    padding: '6px 16px',
    borderRadius: '4px',
    border: '1px solid #f44336',
    backgroundColor: 'transparent',
    color: '#f44336',
    cursor: 'pointer',
    fontSize: '13px',
  },
};
